import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loginUser } from '../store/slices/authSlice';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
  Skeleton,
  useTheme,
  Fade,
  Grow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search,
  FilterList,
  LocalShipping,
  CheckCircle,
  Cancel,
  AccessTime,
  Settings,
  Visibility,
  Inventory,
  TrendingUp,
  AttachMoney,
  Refresh,
  CalendarToday,
  Payment,
  CreditCard
} from '@mui/icons-material';
import { orderService } from '../services/orderService';
import { getProductPrimaryImageUrl, getFullImageUrl } from '../utils/imageUtils';
import ProductImage from '../components/common/ProductImage';

const OrderManagement = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  // State management
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all', // Default to all orders
    dateRange: 'all',
    customerSearch: ''
  });

  // Statistics state
  const [statistics, setStatistics] = useState({
    activeOrders: 0,
    totalOrders: 0,
    monthlyRevenue: 0
  });

  // Refs to prevent duplicate API calls
  const statisticsFetchedRef = useRef(false);
  const isFetchingOrdersRef = useRef(false);
  const isFetchingStatisticsRef = useRef(false);
  const previousUserIdRef = useRef(null);

  // Dialog state for order details
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Status options for dropdown
  const statusOptions = [
    { value: 'all', label: 'All Orders' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'SHIPPED', label: 'Shipped' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'CANCELLED', label: 'Cancelled' }
  ];

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 3 Months' },
    { value: '1y', label: 'Last Year' }
  ];



  // Status badge configuration
  const statusConfig = {
    PENDING: {
      color: 'warning',
      icon: AccessTime,
      label: 'Pending'
    },
    CONFIRMED: {
      color: 'info',
      icon: CheckCircle,
      label: 'Confirmed'
    },
    PROCESSING: {
      color: 'secondary',
      icon: Settings,
      label: 'Processing'
    },
    SHIPPED: {
      color: 'info',
      icon: LocalShipping,
      label: 'Shipped'
    },
    OUT_FOR_DELIVERY: {
      color: 'primary',
      icon: LocalShipping,
      label: 'Out for Delivery'
    },
    DELIVERED: {
      color: 'success',
      icon: CheckCircle,
      label: 'Delivered'
    },
    COMPLETED: {
      color: 'success',
      icon: CheckCircle,
      label: 'Completed'
    },
    CANCELLED: {
      color: 'error',
      icon: Cancel,
      label: 'Cancelled'
    },
    REFUNDED: {
      color: 'error',
      icon: Cancel,
      label: 'Refunded'
    },
    RETURNED: {
      color: 'error',
      icon: Cancel,
      label: 'Returned'
    },
    FAILED: {
      color: 'error',
      icon: Cancel,
      label: 'Failed'
    },
    ON_HOLD: {
      color: 'warning',
      icon: AccessTime,
      label: 'On Hold'
    }
  };

  // Real data fetching functions - memoized to prevent recreation
  const fetchOrders = useCallback(async () => {
    if (!user?.id || isFetchingOrdersRef.current) return;
    
    isFetchingOrdersRef.current = true;
    setLoading(true);
    try {
      let response;
      
      // Check if we have any active filters
      const hasFilters = filters.status !== 'all' || filters.dateRange !== 'all' || filters.customerSearch.trim() !== '';
      
      if (hasFilters) {
        // Use filtered endpoint
        const filterParams = {};
        if (filters.status !== 'all') {
          filterParams.status = filters.status === 'active' 
            ? ['PENDING', 'CONFIRMED', 'PROCESSING', 'SHIPPED', 'OUT_FOR_DELIVERY']
            : [filters.status];
        }
        if (filters.dateRange !== 'all') {
          filterParams.dateRange = filters.dateRange;
        }
        if (filters.customerSearch.trim()) {
          filterParams.customerSearch = filters.customerSearch.trim();
        }
        
        response = await orderService.getFilteredSellerOrders(user.id, filterParams);
      } else {
        // Use regular endpoint for all orders
        response = await orderService.getSellerOrders(user.id);
      }
      
      setOrders(response.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
      isFetchingOrdersRef.current = false;
    }
  }, [user?.id, filters.status, filters.dateRange, filters.customerSearch]);

  const fetchStatistics = useCallback(async () => {
    if (!user?.id || isFetchingStatisticsRef.current || statisticsFetchedRef.current) return;
    
    isFetchingStatisticsRef.current = true;
    statisticsFetchedRef.current = true;
    
    try {
      // Get basic order data to calculate statistics
      const response = await orderService.getSellerOrders(user.id, { page: 0, size: 1000 });
      const orders = response.orders || [];
      const analytics = response.analytics || {};
      
      // Calculate active orders (not delivered, cancelled, or refunded)
      const activeOrders = orders.filter(order => 
        !['DELIVERED', 'CANCELLED', 'REFUNDED', 'COMPLETED'].includes(order.status)
      ).length;
      
      setStatistics({
        activeOrders: activeOrders,
        totalOrders: orders.length,
        monthlyRevenue: analytics.monthlyRevenue || 0
      });
    } catch (error) {
      console.error('Error fetching statistics:', error);
      setStatistics({
        activeOrders: 0,
        totalOrders: 0,
        monthlyRevenue: 0
      });
    } finally {
      isFetchingStatisticsRef.current = false;
    }
  }, [user?.id]);

  // Filter orders based on current filters
  const filteredOrders = useMemo(() => {
    let filtered = [...orders];

    // Status filter
    if (filters.status === 'active') {
      filtered = filtered.filter(order => 
        ['PENDING', 'CONFIRMED', 'PROCESSING', 'SHIPPED', 'OUT_FOR_DELIVERY'].includes(order.status)
      );
    } else if (filters.status !== 'all') {
      filtered = filtered.filter(order => order.status === filters.status);
    }

    // Customer search filter
    if (filters.customerSearch) {
      const searchTerm = filters.customerSearch.toLowerCase();
      filtered = filtered.filter(order =>
        order.customerName?.toLowerCase().includes(searchTerm) ||
        order.customerEmail?.toLowerCase().includes(searchTerm) ||
        order.orderNumber?.toLowerCase().includes(searchTerm)
      );
    }

    return filtered;
  }, [orders, filters]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    if (user?.id) {
      fetchOrders();
    }
  };

  // Handle deliver order
  const handleDeliverOrder = async (orderId) => {
    try {
      await orderService.updateOrderStatus(orderId, 'DELIVERED');
      console.log('Order delivered successfully:', orderId);
      fetchOrders(); // Refresh orders
      fetchStatistics(); // Refresh statistics
    } catch (error) {
      console.error('Error delivering order:', error);
    }
  };

  // Handle view order
  const handleViewOrder = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (order) {
      setSelectedOrder(order);
      setOrderDialogOpen(true);
    }
  };

  // Handle close order dialog
  const handleCloseOrderDialog = () => {
    setOrderDialogOpen(false);
    setSelectedOrder(null);
  };

  // Temporary demo login function
  const handleDemoLogin = async (email, password) => {
    try {
      await dispatch(loginUser({ email, password })).unwrap();
      console.log('Demo login successful');
    } catch (error) {
      console.error('Demo login failed:', error);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Status Badge Component
  const StatusBadge = ({ status }) => {
    const config = statusConfig[status] || statusConfig.PENDING;
    const IconComponent = config.icon;

    return (
      <Chip
        icon={<IconComponent sx={{ fontSize: '16px !important' }} />}
        label={config.label}
        color={config.color}
        variant="outlined"
        size="small"
        sx={{
          fontWeight: 500,
          '& .MuiChip-icon': {
            fontSize: 16
          }
        }}
      />
    );
  };

  // Statistics Card Component
  const StatCard = ({ title, value, icon: Icon, color, delay = 0 }) => (
    <Grow in={true} timeout={800} style={{ transitionDelay: `${delay}ms` }}>
      <Card 
        elevation={0}
        sx={{ 
          height: '100%',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[8],
            borderColor: color
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ fontWeight: 'bold', color: '#000000', mb: 1 }}
              >
                {title}
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700,
                  color: 'text.primary',
                  lineHeight: 1.2
                }}
              >
                {value}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Icon sx={{ fontSize: 24, color: 'white' }} />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Grow>
  );

  // Effect to load statistics only once on mount or when user changes
  useEffect(() => {
    // Reset statistics ref when user changes
    if (user?.id) {
      statisticsFetchedRef.current = false;
      fetchStatistics();
    }
  }, [user?.id, fetchStatistics]);

  // Effect to load orders when filters change (separate from statistics)
  useEffect(() => {
    if (user?.id) {
      fetchOrders();
    }
  }, [user?.id, filters.status, filters.dateRange, filters.customerSearch, fetchOrders]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="xl">
        {/* Temporary Demo Login Section */}
        {!user && (
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            border: '1px solid #e0e0e0', 
            borderRadius: 1, 
            backgroundColor: '#f5f5f5' 
          }}>
            <Typography variant="h6" gutterBottom>
              Demo Login (Testing Only)
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => handleDemoLogin('seller1@premiumstones.com', 'Seller@123')}
              >
                Login as Seller 1 (Premium Stones)
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => handleDemoLogin('seller2@marblemasters.com', 'Seller@123')}
              >
                Login as Seller 2 (Marble Masters)
              </Button>
            </Box>
          </Box>
        )}

        {/* Header Section */}
        <Fade in={true} timeout={600}>
          <Box sx={{ mb: 4 }}>
            {/* Page Title */}
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 1,
                    fontSize: { xs: '2rem', md: '2.5rem' }
                  }}
                >
                  Order Management
                </Typography>
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                  sx={{ fontSize: '1.1rem' }}
                >
                  Monitor and manage your orders efficiently
                  {user && ` - Logged in as: ${user.email}`}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => {
                  fetchOrders();
                  fetchStatistics();
                }}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Refresh
              </Button>
            </Box>
            
            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <StatCard
                  title="Active Orders"
                  value={statistics.activeOrders.toLocaleString()}
                  icon={Inventory}
                  color="primary.main"
                  delay={0}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <StatCard
                  title="Total Orders"
                  value={statistics.totalOrders.toLocaleString()}
                  icon={TrendingUp}
                  color="success.main"
                  delay={100}
                />
              </Grid>
              {/* <Grid item xs={12} md={4}>
                <StatCard
                  title="Monthly Revenue"
                  value={formatCurrency(statistics.monthlyRevenue)}
                  icon={AttachMoney}
                  color="warning.main"
                  delay={200}
                />
              </Grid> */}
            </Grid>

            {/* Filter Controls */}
            <Grow in={true} timeout={800} style={{ transitionDelay: '300ms' }}>
              <Card 
                elevation={0}
                sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}
                  >
                    Filter Orders
                  </Typography>
                  <Grid container spacing={3} alignItems="flex-end">
                     {/* Status Filter */}
                     <Grid item xs={12} sm={6} md={3}>
                       <FormControl fullWidth>
                         <InputLabel>Status</InputLabel>
                         <Select
                           value={filters.status}
                           onChange={(e) => handleFilterChange('status', e.target.value)}
                           label="Status"
                           startAdornment={
                             <InputAdornment position="start">
                               <FilterList sx={{ color: 'text.secondary', fontSize: 20 }} />
                             </InputAdornment>
                           }
                           sx={{ borderRadius: 2 }}
                         >
                           {statusOptions.map(option => (
                             <MenuItem key={option.value} value={option.value}>
                               {option.label}
                             </MenuItem>
                           ))}
                         </Select>
                       </FormControl>
                     </Grid>

                     {/* Date Range Filter */}
                     <Grid item xs={12} sm={6} md={3}>
                       <FormControl fullWidth>
                         <InputLabel>Date Range</InputLabel>
                         <Select
                           value={filters.dateRange}
                           onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                           label="Date Range"
                           startAdornment={
                             <InputAdornment position="start">
                               <CalendarToday sx={{ color: 'text.secondary', fontSize: 20 }} />
                             </InputAdornment>
                           }
                           sx={{ borderRadius: 2 }}
                         >
                           {dateRangeOptions.map(option => (
                             <MenuItem key={option.value} value={option.value}>
                               {option.label}
                             </MenuItem>
                           ))}
                         </Select>
                       </FormControl>
                     </Grid>

                     {/* Customer Search */}
                     <Grid item xs={12} sm={6} md={4}>
                       <TextField
                         fullWidth
                         label="Customer Search"
                         placeholder="Search by name or location..."
                         value={filters.customerSearch}
                         onChange={(e) => handleFilterChange('customerSearch', e.target.value)}
                         InputProps={{
                           startAdornment: (
                             <InputAdornment position="start">
                               <Search sx={{ color: 'text.secondary' }} />
                             </InputAdornment>
                           ),
                         }}
                         sx={{ 
                           '& .MuiOutlinedInput-root': {
                             borderRadius: 2
                           }
                         }}
                       />
                     </Grid>

                     {/* Apply Filters Button */}
                     <Grid item xs={12} sm={6} md={2}>
                       <Button
                         fullWidth
                         variant="contained"
                         onClick={handleApplyFilters}
                         sx={{ 
                           py: 1.75,
                           borderRadius: 2,
                           textTransform: 'none',
                           fontWeight: 600,
                           fontSize: '1rem'
                         }}
                       >
                         Apply Filters
                       </Button>
                     </Grid>
                   </Grid>
                </CardContent>
              </Card>
            </Grow>
          </Box>
        </Fade>

        {/* Main Content Area - Data Table */}
        <Grow in={true} timeout={1000} style={{ transitionDelay: '400ms' }}>
          <Card 
            elevation={0}
            sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            {/* Table Header */}
            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Inventory sx={{ fontSize: 24 }} />
                Orders ({filteredOrders.length})
              </Typography>
            </Box>

            {/* Table Container */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Order ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Product</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.primary' }}>Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <TableRow key={index}>
                        {Array.from({ length: 8 }).map((_, cellIndex) => (
                          <TableCell key={cellIndex}>
                            <Skeleton variant="text" width="80%" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : filteredOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Box 
                          sx={{ 
                            py: 8,
                            textAlign: 'center',
                            color: 'text.secondary'
                          }}
                        >
                          <Inventory 
                            sx={{ 
                              fontSize: 64,
                              color: 'grey.300',
                              mb: 2
                            }} 
                          />
                          <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                            No orders found
                          </Typography>
                          <Typography variant="body2">
                            Try adjusting your filters to see more results.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredOrders.map((order) => (
                      <TableRow 
                        key={order.id} 
                        sx={{ 
                          '&:hover': { 
                            bgcolor: 'action.hover'
                          },
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            #{order.orderNumber || order.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {order.customerName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {order.customerEmail}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {order.items && order.items.length > 0 
                              ? order.items.length > 1 
                                ? `${order.items[0].productName} (+${order.items.length - 1} more)`
                                : order.items[0].productName
                              : 'N/A'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {order.items && order.items.length > 0 
                              ? order.items.reduce((total, item) => total + (item.quantity || 0), 0)
                              : 0
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(order.totalAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={order.status} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(order.orderDate || order.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Visibility />}
                              onClick={() => handleViewOrder(order.id)}
                              sx={{ 
                                textTransform: 'none',
                                borderRadius: 1.5,
                                minWidth: 'auto'
                              }}
                            >
                              View
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grow>
      </Container>

      {/* Order Details Dialog */}
      <Dialog 
        open={orderDialogOpen} 
        onClose={handleCloseOrderDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Order Details - #{selectedOrder?.orderNumber || selectedOrder?.id}
            </Typography>
            {selectedOrder?.status && (
              <StatusBadge status={selectedOrder.status} />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Grid container spacing={3}>
              {/* Order Information */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    Order Information
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant="body2">
                      <strong>Order ID:</strong> #{selectedOrder.orderNumber || selectedOrder.id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Order Date:</strong> {formatDate(selectedOrder.orderDate || selectedOrder.createdAt)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Amount:</strong> {formatCurrency(selectedOrder.totalAmount)}
                    </Typography>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Payment Status:</strong>
                      </Typography>
                      <Chip
                        label={selectedOrder.paymentStatus || 'PENDING'}
                        color={
                          selectedOrder.paymentStatus === 'PAID' || selectedOrder.paymentStatus === 'COMPLETED'
                            ? 'success'
                            : selectedOrder.paymentStatus === 'FAILED'
                            ? 'error'
                            : 'warning'
                        }
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Payment Method:</strong>
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {selectedOrder.paymentMethod === 'UPI' && <Payment sx={{ fontSize: 18, color: 'primary.main' }} />}
                        {selectedOrder.paymentMethod === 'CARD' && <CreditCard sx={{ fontSize: 18, color: 'primary.main' }} />}
                        {selectedOrder.paymentMethod === 'COD' && <AttachMoney sx={{ fontSize: 18, color: 'success.main' }} />}
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {selectedOrder.paymentMethod || 'COD'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Card>
              </Grid>

              {/* Shipping Address */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    Shipping Address
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2">
                      <strong>{selectedOrder.customerName}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedOrder.customerEmail}
                    </Typography>
                    {selectedOrder.shippingAddress && typeof selectedOrder.shippingAddress === 'object' ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {selectedOrder.shippingAddress.street && (
                          <Typography variant="body2">
                            {selectedOrder.shippingAddress.street}
                          </Typography>
                        )}
                        {selectedOrder.shippingAddress.landmark && (
                          <Typography variant="body2">
                            {selectedOrder.shippingAddress.landmark}
                          </Typography>
                        )}
                        <Typography variant="body2">
                          {[
                            selectedOrder.shippingAddress.city,
                            selectedOrder.shippingAddress.state,
                            selectedOrder.shippingAddress.postalCode
                          ].filter(Boolean).join(', ')}
                        </Typography>
                        {selectedOrder.shippingAddress.country && (
                          <Typography variant="body2">
                            {selectedOrder.shippingAddress.country}
                          </Typography>
                        )}
                        {selectedOrder.shippingAddress.addressType && (
                          <Typography variant="caption" color="text.secondary">
                            Address Type: {selectedOrder.shippingAddress.addressType}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2">
                        {selectedOrder.shippingAddress || 'Address not available'}
                      </Typography>
                    )}
                  </Box>
                </Card>
              </Grid>

              {/* Order Items */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    Order Items ({selectedOrder.items?.length || 0})
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Product</strong></TableCell>
                          <TableCell align="center"><strong>Quantity</strong></TableCell>
                          <TableCell align="right"><strong>Unit Price</strong></TableCell>
                          <TableCell align="right"><strong>Total</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedOrder.items && selectedOrder.items.length > 0 ? (
                          selectedOrder.items.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <ProductImage
                                    item={item}
                                    product={item.product}
                                    width={60}
                                    height={60}
                                    variant="rounded"
                                    sx={{
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                  />
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {item.productName || 'Product Name'}
                                    </Typography>
                                    {/* <Typography variant="caption" color="text.secondary">
                                      SKU: {item.sku || item.productSku || 'N/A'}
                                    </Typography> */}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Chip 
                                  label={item.quantity || 1} 
                                  size="small" 
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(item.unitPrice || 0)}
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {formatCurrency((item.quantity || 1) * (item.unitPrice || 0))}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography variant="body2" color="text.secondary">
                                No items found
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseOrderDialog} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderManagement;